import { Link } from "@tanstack/react-router";
import { formatDate } from "@ghezaa/shell";
import { Button, PageHeader, Table, TBody, TD, TH, THead, TR } from "@ghezaa/ui";
import type { ReportCatalogue, ReportView } from "@/api/client";
import { useReportCatalogue, useReportViews } from "@/api/queries";
import { useCurrentMarket } from "@/lib/market";
import { newSpec, summary } from "@/lib/reports";

/** Reports: the person's saved reports, those shared in the market, and the starting points most teams ask for first. */
export function ReportsPage() {
  const market = useCurrentMarket();
  const catalogue = useReportCatalogue(market.code);
  const views = useReportViews(market.code);
  const mine = views.data?.mine ?? [];
  const shared = views.data?.shared ?? [];

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title="Reports"
        description="Sales figures by any dimension: open a starting point or a saved report, or make a new one."
        actions={
          <>
            <Button asChild variant="ghost">
              <Link to="/$market/reports/definitions" params={{ market: market.code }}>
                Definitions
              </Link>
            </Button>
            <Button asChild variant="primary">
              <Link to="/$market/reports/new" params={{ market: market.code }} search={{ spec: newSpec }}>
                New report
              </Link>
            </Button>
          </>
        }
      />
      {views.isError && <p className="bg-danger-soft px-3 py-2 text-sm text-danger">Could not load the saved reports: {views.error.message}</p>}

      {mine.length > 0 && <Saved title="Yours" views={mine} catalogue={catalogue.data} market={market.code} others={false} />}
      {shared.length > 0 && <Saved title={`Shared in ${market.name}`} views={shared} catalogue={catalogue.data} market={market.code} others />}

      <section className="flex min-w-0 flex-col gap-3">
        <h2 className="font-display text-xl text-ink">Starting points</h2>
        {!catalogue.data && <div className="h-40 bg-surface-2" aria-busy="true" />}
        {catalogue.data && (
          <Table>
            <THead>
              <TR>
                <TH>Report</TH>
                <TH from="md">Shows</TH>
              </TR>
            </THead>
            <TBody>
              {catalogue.data.starters.map((s) => (
                <TR key={s.key} linked>
                  <TD className="w-full max-w-0">
                    <Link to="/$market/reports/new" params={{ market: market.code }} search={{ spec: s.spec, starter: s.key }} className="block truncate">
                      {s.name}
                    </Link>
                    <span className="block truncate text-xs text-ink-3">{s.description}</span>
                  </TD>
                  <TD from="md" className="whitespace-nowrap text-sm text-ink-2">
                    {summary(s.spec, catalogue.data)}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </section>
    </div>
  );
}

/** Saved reports: the person's own (when each last changed, and whether it is shared), or others' shared ones (who saved them). */
function Saved({ title, views, catalogue, market, others }: { title: string; views: ReportView[]; catalogue: ReportCatalogue | undefined; market: string; others: boolean }) {
  return (
    <section className="flex min-w-0 flex-col gap-3">
      <h2 className="font-display text-xl text-ink">{title}</h2>
      <Table>
        <THead>
          <TR>
            <TH>Report</TH>
            <TH from="md">Shows</TH>
            <TH from="lg" className="whitespace-nowrap">{others ? "Saved by" : "Changed"}</TH>
          </TR>
        </THead>
        <TBody>
          {views.map((v) => (
            <TR key={v.id} linked>
              <TD className="w-full max-w-0">
                <Link to="/$market/reports/$id" params={{ market, id: v.id }} className="block truncate">
                  {v.name}
                </Link>
                {!others && v.shared && <span className="block truncate text-xs text-ink-3">Shared with everyone in the market</span>}
                <span className="block truncate text-xs text-ink-3 md:hidden">{v.spec ? summary(v.spec, catalogue) : "No longer opens"}</span>
              </TD>
              <TD from="md" className="whitespace-nowrap text-sm text-ink-2">
                {v.spec ? summary(v.spec, catalogue) : "No longer opens: delete it"}
              </TD>
              <TD from="lg" className="whitespace-nowrap text-xs tabular-nums text-ink-2">
                {others ? v.ownerName : formatDate(v.updatedAt)}
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </section>
  );
}

/** What every figure in Reports counts and what its rows can be: the catalogue's own words. */
export function DefinitionsPage() {
  const market = useCurrentMarket();
  const catalogue = useReportCatalogue(market.code);
  const c = catalogue.data;

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        eyebrow={
          <Link to="/$market/reports" params={{ market: market.code }} className="hover:text-ink">
            Reports ›
          </Link>
        }
        title="Definitions"
        description="What every figure in Reports counts, from BC's posted invoices and credit memos, before VAT."
      />
      {!c && <div className="h-80 bg-surface-2" aria-busy="true" />}
      {c && (
        <>
          <section className="flex min-w-0 flex-col gap-3">
            <h2 className="font-display text-xl text-ink">Measures</h2>
            <Table>
              <THead>
                <TR>
                  <TH className="whitespace-nowrap">Measure</TH>
                  <TH>What it counts</TH>
                </TR>
              </THead>
              <TBody>
                {c.measures.map((m) => (
                  <TR key={m.key}>
                    <TD className="whitespace-nowrap py-2.5 align-top font-medium">{m.label}</TD>
                    <TD className="py-2.5 align-top text-sm text-ink-2">{m.definition}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </section>
          <section className="flex min-w-0 flex-col gap-3">
            <h2 className="font-display text-xl text-ink">Rows and filters</h2>
            <Table>
              <THead>
                <TR>
                  <TH className="whitespace-nowrap">Dimension</TH>
                  <TH>What it is</TH>
                </TR>
              </THead>
              <TBody>
                {c.dimensions.map((d) => (
                  <TR key={d.key}>
                    <TD className="whitespace-nowrap py-2.5 align-top font-medium">{d.label}</TD>
                    <TD className="py-2.5 align-top text-sm text-ink-2">{d.definition}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </section>
          <section className="flex min-w-0 flex-col gap-3">
            <h2 className="font-display text-xl text-ink">Periods</h2>
            <Table>
              <TBody>
                {periodRules.map(([label, rule]) => (
                  <TR key={label}>
                    <TD className="whitespace-nowrap py-2.5 align-top font-medium">{label}</TD>
                    <TD className="py-2.5 align-top text-sm text-ink-2">{rule}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </section>
        </>
      )}
    </div>
  );
}

const periodRules: [string, string][] = [
  ["This week, month, quarter, year", "From its first day to today, against the same days of the one before. A week runs Sunday to Saturday."],
  ["Last week, month, quarter, year", "The whole of it, against the whole of the one before."],
  ["Last 7, 30 or 90 days, chosen days", "Those days, against as many days just before them."],
  ["Last 12 months", "From the first day of the month eleven months ago to today, against the same days a year before."],
  ["Against last year", "The same dates a year back. Sales has figures from the first month BC has, so a year before it is empty."],
  ["Total", "Worked out over every entry, never the sum of the rows: a customer who bought from two salespeople is one customer."],
];
